import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    validateUser,
    validateUserName,
} from '../../../../services/Validations';
import {
    simpalsUserAutocomplete,
    getEnumRoles,
    userAccessManage,
    userKeyCashRegister,
} from '../../../../services/requests/GraphRequests';
import {
    createUser,
    updateUser,
} from '../../../../services/requests/GraphRequests';
import { ROLE } from '../../../../constants';

export const useUserFields = userInfo => {
    const navigate = useNavigate();
    const [logins, setLogins] = useState([]);
    const [roles, setRoles] = useState([]);
    const [existingAccess, setExistingAccesses] = useState([]);
    const [existingCashRegisters, setExistingCashRegisters] = useState([]);
    const [readyToSave, setReadyToSave] = useState(true);
    const [openSuccess, setOpenSuccess] = useState(false);
    const [alertText, setAlertText] = useState('');
    const [deletionIndex, setDeletionIndex] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [removeCashRegisters, setRemoveCashRegisters] = useState(false);
    const open = Boolean(anchorEl);
    const idPopup = open ? 'removePopup' : undefined;

    const handleOpenPopup = (event, index, removeCashRegister = false) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
        setDeletionIndex(index);
        if (!removeCashRegister) {
            setRemoveCashRegisters(true);
        }
    };

    const [error, setError] = useState({
        status: false,
        msg: 'Error',
    });
    const [user, setUser] = useState({
        name: '',
        id: '',
        simpalsId: '',
        simpalsLogin: '',
        accesses: [{ role: '' }],
        cashRegisters: [],
    });

    const organizationRoles = roles
        .filter(role => role.name.includes('ORGANIZATION'))
        .map(role => role.name);

    const userRoles = user.accesses.filter(
        access => !access.role.includes('SYSTEM'),
    );

    const { simpalsLogin, simpalsId, id, name, accesses, cashRegisters } =
        userInfo || {
            simpalsLogin: '',
            simpalsId: '',
            name: '',
            accesses: [],
            organizationId: null,
            id: '',
            currentAccess: '',
            cashRegisters: [],
        };

    useEffect(() => {
        if (userInfo) {
            setReadyToSave(validateUserName(user));
        } else {
            setReadyToSave(validateUser(user, organizationRoles, ROLE));
        }
    }, [user, setUser]);

    useEffect(() => {
        if (userInfo) {
            setUser({
                name: name,
                id: id,
                simpalsId: simpalsId,
                simpalsLogin: simpalsLogin,
                accesses: accesses,
                cashRegisters: cashRegisters,
            });
            const accessesIds = accesses.map(access => access.id);
            setExistingAccesses(accessesIds);
            const cashRegistersIds = cashRegisters.map(
                register => register.cashRegisterId,
            );
            setExistingCashRegisters(cashRegistersIds);
        }
        getEnumRoles()
            .then(data => {
                setRoles(
                    data.data.__type.enumValues.filter(role =>
                        role.name.includes('ORGANIZATION'),
                    ),
                );
            })
            .catch(error => {
                // eslint-disable-next-line no-console
                console.log(error);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (openSuccess) {
            setTimeout(() => {
                navigate(`/client/users`);
            }, 3500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openSuccess, setOpenSuccess]);

    const handleChangeUser = userField => {
        setUser({
            ...user,
            ...userField,
        });
    };

    const handleLoginInput = event => {
        const value = event?.target?.value || '';
        if (value) {
            if (value) {
                simpalsUserAutocomplete(value)
                    .then(data => {
                        const loginsData = data.data.simpalsUserAutocomplete;
                        setLogins(loginsData ? loginsData : []);
                    })
                    .catch(error => {
                        // eslint-disable-next-line no-console
                        console.log(error.message);
                    });
            } else {
                setLogins([]);
            }
        }
    };

    const handleChangeAccess = (accessIndex, newUserAccess = '') => {
        let newAccesses = [];
        if (newUserAccess) {
            newAccesses = user.accesses.map((userAccess, index) => {
                if (index === accessIndex) {
                    return { ...userAccess, ...newUserAccess };
                } else {
                    return userAccess;
                }
            });
        } else {
            newAccesses = user.accesses.filter((userAccess, index) => {
                return index !== accessIndex;
            });
        }
        setUser({
            ...user,
            accesses: newAccesses,
        });
    };

    const handleChangeCashRegister = cashRegisters => {
        setUser(prevUser => ({
            ...prevUser,
            cashRegisters,
        }));
    };

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpenSuccess(false);
        setError({
            status: false,
            msg: 'Error',
        });
    };

    const handleAccessManage = (accessIndex, accessCreate = true) => {
        let userNewAccess = {};
        if (!accessCreate) setAnchorEl(null);
        user.accesses.forEach((userAccess, index) => {
            if (index === accessIndex) {
                userNewAccess = {
                    create: accessCreate,
                    role: userAccess.role,
                    userId: user.id,
                };
            }
        });
        userAccessManage(userNewAccess)
            .then(data => {
                if (data?.data?.userAccessManage !== null) {
                    const { user, errors } = data.data.userAccessManage;
                    if (user && errors?.length === 0) {
                        const newAccessesIds = user.accesses.map(
                            access => access.id,
                        );
                        setExistingAccesses(newAccessesIds);
                        const newAccesses = user.accesses;
                        setUser({ ...user, accesses: newAccesses });
                    } else {
                        let msgError = 'There are errors while managing roles';
                        if (errors?.length > 0) {
                            msgError = errors[0].message;
                        }
                        setError({
                            status: true,
                            msg: msgError,
                        });
                    }
                }
            })
            .catch(error => {
                setError({
                    status: true,
                    msg: error?.message || 'error',
                });
            });
    };

    const handleCashRegisterAccess = cashRegister => {
        userKeyCashRegister(cashRegister)
            .then(data => {
                if (data?.data?.userApiKeyRefresh !== null) {
                    const { user, errors } = data.data.userApiKeyRefresh;
                    if (user && errors?.length === 0) {
                        const newCashRegisters = user.cashRegisters.map(
                            register => {
                                return {
                                    ...register,
                                    active: true,
                                };
                            },
                        );
                        handleChangeCashRegister(newCashRegisters);
                    } else {
                        let msgError = 'There are errors while managing roles';
                        if (errors?.length > 0) {
                            msgError = errors[0].message;
                        }
                        setError({
                            status: true,
                            msg: msgError,
                        });
                    }
                }
            })
            .catch(error => {
                setError({
                    status: true,
                    msg: error?.message || 'error',
                });
            });
    };

    const saveUser = () => {
        delete user.simpalsLogin;
        createUser(user)
            .then(data => {
                if (data?.data?.userCreate !== null) {
                    if (data.data.userCreate.errors.length === 0) {
                        setOpenSuccess(true);
                        setAlertText('User created!');
                    }
                } else {
                    let msgError = 'There are errors while user created';
                    if (data?.data?.errors?.length > 0) {
                        msgError = data.data.errors[0].message;
                    }
                    setError({
                        status: true,
                        msg: msgError,
                    });
                }
            })
            .catch(error => {
                setError({
                    status: true,
                    msg: error?.message || 'error',
                });
            });
    };

    const editUser = () => {
        updateUser(user)
            .then(data => {
                if (data?.data?.userUpdate !== null) {
                    if (data.data.userUpdate.errors.length === 0) {
                        setOpenSuccess(true);
                        setAlertText('User name edited!');
                    }
                } else {
                    let msgError = 'There are errors while user updated';
                    if (data.data.errors.length > 0) {
                        msgError = data.data.errors[0].message;
                    }
                    setError({
                        status: true,
                        msg: msgError,
                    });
                }
            })
            .catch(error => {
                setError({
                    status: true,
                    msg: error?.message || 'error',
                });
            });
    };

    const addNewOrganizationRole = () => {
        setUser({
            ...user,
            accesses: [...user.accesses, { role: '' }],
        });
    };

    return {
        user,
        roles,
        userRoles,
        logins,
        existingAccess,
        existingCashRegisters,
        deletionIndex,
        open,
        anchorEl,
        setAnchorEl,
        idPopup,
        readyToSave,
        openSuccess,
        error,
        alertText,
        removeCashRegisters,
        handleClose,
        handleOpenPopup,
        handleChangeUser,
        handleLoginInput,
        handleChangeAccess,
        handleChangeCashRegister,
        handleAccessManage,
        handleCashRegisterAccess,
        saveUser,
        editUser,
        addNewOrganizationRole,
    };
};
